import { lazy, ComponentType } from 'react';

/**
 * A wrapper around React.lazy that automatically retries failed chunk loads.
 *
 * When a new version is deployed, users with the old index.html may try to
 * load chunks that no longer exist. This helper:
 *   1. Catches the initial import error
 *   2. Retries the import once (the browser will fetch the new chunk URL)
 *   3. If the retry also fails, forces a full page reload (which fetches the
 *      new index.html and all new chunk references)
 *   4. Uses sessionStorage to prevent infinite reload loops
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch(() => {
      // First failure — retry the import once. The browser may have cached a
      // stale module map but the retry can succeed if the chunk URL changed.
      return factory().catch(() => {
        // Second failure — the chunks are truly gone. Reload the page to pick
        // up the new index.html, but guard against infinite reload loops.
        const RELOAD_KEY = 'enough_chunk_reload';
        const lastReload = sessionStorage.getItem(RELOAD_KEY);
        const now = Date.now();

        // Only auto-reload if we haven't reloaded in the last 10 seconds
        if (!lastReload || now - Number(lastReload) > 10_000) {
          sessionStorage.setItem(RELOAD_KEY, String(now));
          window.location.reload();
        }

        // If we already reloaded recently, let the error propagate to the
        // ErrorBoundary so the user sees a helpful message instead of a loop.
        return Promise.reject(
          new Error('A new version of the app is available. Please refresh the page.')
        );
      });
    })
  );
}
